import { CacheHandlerOptions } from '@app/utils/cache/cache-handler-options';
import { StringMap } from '@app/utils/string-map';
import { Observable, of } from 'rxjs';
import { delay, share, tap } from 'rxjs/operators';

export class CacheHandler {
  private static _cacheHandler: CacheHandler;

  public static get(): CacheHandler {
    if (CacheHandler._cacheHandler == null) {
      CacheHandler._cacheHandler = new CacheHandler();
    }

    return CacheHandler._cacheHandler;
  }

  private projectionCache: StringMap<any> = {};

  /**
   * Handle the caching of observables, i.e.:
   *
   *  * Evict the cache for the given key when evict is passed.
   *  * Return the cached value if it exists.
   *  * Check if there is already a request underway for the given key. If so return the original
   *    observable (which is shared and can be used at multiple locations).
   *  * If there is no cached value and no request underway, run the request and save it until it
   *    is finished so there are no duplicate requests.
   */
  public handle<T>(options: CacheHandlerOptions<T>): Observable<T> {
    let result$: Observable<T>;

    const cache = options.cacheStrategy;
    const key = options.key;

    if (options.evict) {
      cache.evict(options.key);
    }

    const cachedValue = cache.get<T>(key);

    if (cachedValue) {
      // delay is necessary because the datatable dtInstance is not set without it
      result$ = of(cachedValue).pipe(delay(10));
    } else if (this.projectionCache[key]) {
      result$ = this.projectionCache[key];
    } else {
      // Call the original flatMap function
      this.projectionCache[key] = options.method().pipe(
        tap((result) => {
          cache.set(key, result, options.ttl);
          delete this.projectionCache[key];
        }),
        share()
      );

      result$ = this.projectionCache[key];
    }

    return result$;
  }
}
